import type { RefObject } from 'react';

import type { DataTableV2AlignmentColumnDef } from '../features/ColumnAlignment.js';

function getActionsMenuAlignment(
  alignment: DataTableV2AlignmentColumnDef['alignment'],
): 'center' | 'end' | 'start' {
  if (alignment?.endsWith('center')) {
    return 'center';
  } else if (alignment?.endsWith('right')) {
    return 'end';
  }

  return 'start';
}

/**
 * Gets the alignment for the menu based on the alignment of the column definition. If the header cell or
 * table cell are outside the bounds of the table's borders, sets the alignment based on the available space
 * and the size of the menu.
 *
 * @param columnAlignment - The alignment of the column based on the column definition.
 * @param buttonRef - A ref to the menu trigger.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns 'center' | 'end' | 'start'
 * @internal
 */
export function useActionsMenuAlignment(
  columnAlignment: DataTableV2AlignmentColumnDef['alignment'],
  buttonRef: RefObject<HTMLButtonElement | null> | null,
  virtualizationContainer?: HTMLDivElement,
): 'center' | 'end' | 'start' {
  // Get menu alignment based on column alignment
  let alignment = getActionsMenuAlignment(columnAlignment);

  // Return alignment if refs are undefined or if the table has no scroll bar
  if (
    !buttonRef?.current ||
    virtualizationContainer === undefined ||
    virtualizationContainer.scrollWidth <= virtualizationContainer.clientWidth
  ) {
    return alignment;
  }

  // Table has a scroll bar, calculate alignment based on location of button
  // in the table
  const buttonRect = buttonRef.current.getBoundingClientRect();
  const tableRect = virtualizationContainer.getBoundingClientRect();
  if (
    buttonRect.x <= tableRect.x &&
    buttonRect.x + buttonRect.width <= tableRect.x + tableRect.width
  ) {
    // if the left side of the button is overflowing the table whereas the right side is shown inside of it,
    // then align menu at the end
    alignment = 'end';
  } else if (
    buttonRect.x >= tableRect.x &&
    buttonRect.x + buttonRect.width >= tableRect.x + tableRect.width
  ) {
    // if the right side of the button is overflowing the table whereas the left side is shown inside of it,
    // then align menu at the start
    alignment = 'start';
  } else if (
    buttonRect.x < tableRect.x &&
    buttonRect.x + buttonRect.width > tableRect.x + tableRect.width
  ) {
    // if both sides are overflowing the table, align menu at the center of the button
    alignment = 'center';
  }

  return alignment;
}
